import { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { Translate } from 'next-translate';
import { combineClassNames } from '@arkestro/arkestro-design-system';
import { UndoOutlined } from '@ant-design/icons';

import { ID, LineItem, Lot } from 'types';
import { DropdownMenuSettings as DetailedSettings } from '@components/line-items-grid/types';
import { LineItemsAction, LineItemChangeActionPayload } from '@providers/LineItemsProvider/action_types';
import { LINE_ITEM_CHANGE, SHOULD_RERENDER_LINE_ITEM_DATA } from '@providers/LineItemsProvider/constants';
import { REQUEST_TYPE_PRICE_UPDATE } from '@components/bid_request/RequestConstants';
import { SUGGESTED_PRICE_COLUMN_KEY } from '@components/bid_request/details/constants';

import {
  canAddAdderColumn,
  canAddColumn,
  canEditColumn,
  canRemoveColumn,
  ColumnToDelete,
  getEditColumnName,
  getRemoveColumnName,
  hideMenuOptions,
  onAddAdderColumn,
  onAddColumn,
  onEditColumn,
  onRemoveColumn,
} from './dropdown';
import { getHeaderID, getRendererWithIcon, getInitSettings } from './dropdown/utils';
import type HandsontableCore from 'handsontable';

interface GenerateDropdownMenuParams {
  dispatch: Dispatch<LineItemsAction>;
  headerMap: ID[];
  hotRef: MutableRefObject<any>;
  isOverAdderLimit?: boolean;
  isOverLimit: boolean;
  isReadOnly?: boolean;
  lot: Lot;
  maxAdderColumnCount?: number;
  maxDescriptiveColumnCount: number;
  paginationEnabled?: boolean;
  requestType?: string;
  setColumnToDelete: (_record: ColumnToDelete) => void;
  setEditingColumnId: (_headerId: SetStateAction<ID | undefined>) => void;
  t: Translate;
}

// Price-update disablement carries its own tooltip; otherwise fall back to the
// read-only/over-limit tooltip computed in getInitSettings.
const getAdderTooltip = ({
  fallbackTooltip,
  isDisabled,
  isPriceUpdate,
  t,
}: {
  fallbackTooltip: string | undefined;
  isDisabled: boolean;
  isPriceUpdate: boolean;
  t: Translate;
}): string => {
  if (!isDisabled) return '';
  if (isPriceUpdate) return t('bid_request.line_items.tooltips.adder_price_update_disabled');

  return fallbackTooltip ?? '';
};

// We hide certain menu options if the column is not a custom column
export const generateDropdownMenu = ({
  dispatch,
  headerMap,
  hotRef,
  isOverAdderLimit = false,
  isOverLimit,
  isReadOnly = false,
  lot,
  maxAdderColumnCount,
  maxDescriptiveColumnCount,
  paginationEnabled = false,
  requestType,
  setColumnToDelete,
  setEditingColumnId: setColumnId,
  t,
}: GenerateDropdownMenuParams): DetailedSettings => {
  const init = getInitSettings({
    isOverAdderLimit,
    isOverLimit,
    isReadOnly,
    maxAdderColumnCount,
    maxDescriptiveColumnCount,
    t,
  });
  const { custom_column_headers: headers } = lot;

  const isPriceUpdate = requestType === REQUEST_TYPE_PRICE_UPDATE;

  const addColumn = {
    callback: (): void => onAddColumn({ dispatch, headerMap, hotRef, isReadOnly, lot, setColumnId }),
    disabled: (): boolean => !canAddColumn({ headerMap, hotRef, isOverLimit }),
    hidden: (): boolean => !hideMenuOptions({ headerMap, hotRef, menuOption: 'add_custom_column' }),
    name: init.addColumn.name,
    renderer: (_hot: unknown, wrapper: HTMLElement): HTMLElement => {
      const isDisabled = !canAddColumn({ headerMap, hotRef, isOverLimit });
      const className = combineClassNames(wrapper.className, !isDisabled ? 'pointer' : '');

      return getRendererWithIcon({ ...init.addColumn, className, wrapper });
    },
  };

  const addAdderColumn = {
    callback: (): void => onAddAdderColumn({ dispatch, headerMap, hotRef, isReadOnly, lot, setColumnId }),
    disabled: (): boolean => isPriceUpdate || !canAddAdderColumn({ headerMap, hotRef, isOverAdderLimit, isReadOnly }),
    // TODO: Reuses 'add_custom_column' condition — may need its own menuOption if adder should only appear on price columns
    hidden: (): boolean => !hideMenuOptions({ headerMap, hotRef, menuOption: 'add_custom_column' }),
    name: init.addAdderColumn.name,
    renderer: (_hot: unknown, wrapper: HTMLElement): HTMLElement => {
      const isDisabled = isPriceUpdate || !canAddAdderColumn({ headerMap, hotRef, isOverAdderLimit, isReadOnly });
      const className = combineClassNames(wrapper.className, !isDisabled ? 'pointer' : '');
      const tooltip = getAdderTooltip({ fallbackTooltip: init.addAdderColumn.tooltip, isDisabled, isPriceUpdate, t });

      return getRendererWithIcon({ ...init.addAdderColumn, className, tooltip, wrapper });
    },
  };

  const editColumn = {
    callback: (): void => onEditColumn({ headerMap, headers, hotRef, isReadOnly, setColumnId }),
    disabled: (): boolean => !canEditColumn({ headerMap, headers, hotRef, isReadOnly }),
    hidden: (): boolean => !hideMenuOptions({ headerMap, hotRef, menuOption: 'edit_custom_column' }),
    name: (): string => getEditColumnName({ headerMap, headers, hotRef, isReadOnly, t }),
    renderer: (_hot: unknown, wrapper: HTMLElement): HTMLElement => {
      const name = getEditColumnName({ headerMap, headers, hotRef, isReadOnly, t });
      const isDisabled = !canEditColumn({ headerMap, headers, hotRef, isReadOnly });
      const className = combineClassNames(wrapper.className, !isDisabled ? 'pointer' : '');
      const tooltip = isDisabled ?
        t('bid_request.line_items.tooltips.disabled_action', {
          action: t('bid_request.line_items.tooltips.actions.edit_visible_columns'),
        }) :
        '';

      return getRendererWithIcon({ ...init.editColumn, className, name, tooltip, wrapper });
    },
  };

  const VISUAL_DIVIDER = {
    disabled: true,
    hidden: (): boolean => !hideMenuOptions({ headerMap, hotRef, menuOption: 'divider' }),
    name: '---------'
  };

  const removeColumn = {
    callback: () => onRemoveColumn({ headerMap, headers, hotRef, isReadOnly, lot, setColumnToDelete, t }),
    disabled: (): boolean => !canRemoveColumn({ headerMap, headers, hotRef, isReadOnly }),
    hidden: (): boolean => !hideMenuOptions({ headerMap, hotRef, menuOption: 'remove_custom_column' }),
    name: (): string => getRemoveColumnName({ headerMap, headers, hotRef, isReadOnly, t }),
    renderer: (_hot: unknown, wrapper: HTMLElement): HTMLElement => {
      const name = getRemoveColumnName({ headerMap, headers, hotRef, isReadOnly, t });
      const isDisabled = !canRemoveColumn({ headerMap, headers, hotRef, isReadOnly });
      const className = combineClassNames(wrapper.className, !isDisabled ? 'remove_col' : '');
      let tooltip = '';
      if (isReadOnly && isDisabled) {
        tooltip = t('bid_request.line_items.tooltips.disabled_action', {
          action: t('bid_request.line_items.tooltips.actions.remove_visible_columns'),
        });
      }
      if (!tooltip?.length && isDisabled) {
        tooltip = t('tooltip.lots.remove_disabled');
      }

      return getRendererWithIcon({ ...init.removeColumn, className, name, tooltip, wrapper });
    },
  };

  // Reset every Suggested Price override on THIS lot back to the AI predictive value. Only visible
  // when the dropdown was opened on the Suggested Price column header — `hidden` gates on the
  // active column via `getHeaderID`. Danger-tinted (red) to signal the destructive nature.
  // Nothing on this lot has an AI suggestion to fall back to — the state immediately after an
  // XLSX import, before the first generation run. Resetting would blank every overridden cell
  // rather than restore a suggestion, so the option is disabled. Mirrors the per-cell reset icon
  // in SuggestedPriceCellRenderer, which hides itself on the same condition per row.
  const lotHasNoPredictivePrices = (lot.line_items ?? [])
    .every((li: LineItem): boolean => li.predictive_unit_price == null);
  const resetSuggestedPricesDisabled = isReadOnly || paginationEnabled || lotHasNoPredictivePrices;

  const resetSuggestedPrices = {
    callback: (): void => {
      if (paginationEnabled) return;
      const lineItems: LineItemChangeActionPayload['lineItems'] = {};

      (lot.line_items ?? []).forEach((li: LineItem): void => {
        // Only rows with an actual override need resetting; rows where the buyer never set
        // anything already display predictive.
        if (li.suggested_price_override == null) return;
        if (li.predictive_unit_price == null) return;
        if (Number(li.suggested_price_override) === Number(li.predictive_unit_price)) return;
        lineItems[li.id] = { suggested_price_override: null };
      });

      if (Object.keys(lineItems).length === 0) return;

      dispatch({
        payload: {
          customColumnItems: {},
          lineItems,
          lotId: lot.id,
          shouldApplyChanges: true,
        },
        type: LINE_ITEM_CHANGE,
      });
      dispatch({
        payload: { shouldRerenderLineItemData: true },
        type: SHOULD_RERENDER_LINE_ITEM_DATA,
      });
    },
    disabled: (): boolean => resetSuggestedPricesDisabled,
    hidden: (): boolean => getHeaderID({ headerMap, hotRef }) !== SUGGESTED_PRICE_COLUMN_KEY,
    name: t('bid_request.line_items.suggested_price.reset_all'),
    renderer: (_hot: HandsontableCore, wrapper: HTMLElement): HTMLElement => {
      // Danger tint only while the option is actionable. The danger class hardcodes
      // `color: $colorErrorBase`, which outranks Handsontable's --ht-disabled-color, so keeping it
      // on a disabled row would render it red-and-unclickable — reading as an enabled destructive
      // action. Dropping it lets the native disabled grey through.
      const className = combineClassNames(
        wrapper.className,
        resetSuggestedPricesDisabled ? '' : 'pointer',
        resetSuggestedPricesDisabled ? '' : 'arkestro-dropdown-menu-item--danger',
      );
      let tooltip = '';
      if (isReadOnly) {
        tooltip = t('bid_request.line_items.tooltips.disabled_action', {
          action: t('bid_request.line_items.suggested_price.reset_all'),
        });
      } else if (paginationEnabled) {
        tooltip = t('bid_request.line_items.tooltips.not_available_paginated');
      } else if (lotHasNoPredictivePrices) {
        tooltip = t('bid_request.line_items.suggested_price.reset_all_no_predictive');
      }

      return getRendererWithIcon({
        className,
        icon: <UndoOutlined />,
        name: t('bid_request.line_items.suggested_price.reset_all'),
        tooltip,
        wrapper,
      });
    },
  };

  // TLC: PEPPERMINT-1082
  // This is a good start here, using hideMenuOptions() we can check if the column is a custom column
  // Based on that we should determine a way to show/hide the [...] menu
  return ({
    items: {
      add_adder_column: addAdderColumn,
      add_column: addColumn,
      div2: VISUAL_DIVIDER,
      edit_column: editColumn,
      remove_col: removeColumn,
      reset_suggested_prices: resetSuggestedPrices,
    },
  });
};
